import { Datum } from "../../../components/docs/Datum.js";
import { note, p, table } from "../blocks.js";
import { QUOTED } from "../thresholds.js";
import type { WikiTopic } from "../types.js";

export const severity: WikiTopic = {
  slug: "severity",
  title: "Severity",
  summary: "Nearly every row ships muted, and that is the measurement talking.",
  group: "concepts",
  sections: [
    {
      heading: "Three levels and one default",
      code: "info / warn / urgent",
      blocks: [
        p(
          <>
            A row is <Datum>info</Datum>, <Datum>warn</Datum>, or <Datum>urgent</Datum>. Delivery
            reads <Datum>alert_rules.min_severity</Datum>, which defaults to <Datum>warn</Datum>, so
            an <Datum>info</Datum> row reaches nobody and lives only in the feed.
          </>,
        ),
        p(
          <>
            That default is what makes muting free. A strategy nobody has measured accumulates
            evidence on screen without costing anyone a notification, and a reader who wants those
            rows back adds one <Datum>alert_rules</Datum> row at <Datum>min_severity: info</Datum>.
            No code changes.
          </>,
        ),
      ],
    },
    {
      heading: "The threshold tables are empty on purpose",
      code: "INTRADAY_THRESHOLDS / SWING_THRESHOLDS",
      blocks: [
        p(
          <>
            Both tables are empty, and neither is an oversight. Every <Datum>magnitude</Datum> in
            this design is strategy-relative: a crossover distance in ATR, a volume multiple, a gap
            in ATR, an RSI distance. The only way to compare them is against a measured
            distribution, and <Datum>pnpm validate:strategies</Datum> is what produces it.
          </>,
        ),
        p(
          <>
            Until the tables are filled from that output, every hourly and swing type resolves to{" "}
            <Datum>info</Datum>. An invented threshold would produce a plausible alert, which is
            worse than no alert.
          </>,
        ),
        note(
          <>
            Rank decile crossings are held at <Datum>info</Datum> by a separate rule, for the same
            reason plus one more: they are the loudest emitter here, roughly <Datum>1,600</Datum>{" "}
            rows a session. At <Datum>warn</Datum> they cleared the default floor and buried the
            measured strategies under a wall of Telegram messages.
          </>,
        ),
      ],
    },
    {
      heading: "What the measurement found",
      blocks: [
        p(
          <>
            Two validation runs, over <Datum>62,544</Datum> and then <Datum>235,470</Datum>{" "}
            symbol-buckets, found <Datum>fourteen of fourteen</Datum> intraday types failing to
            clear the <Datum>8.24 bps</Datum> round trip. That is why the intraday table stayed
            empty after the numbers came back.
          </>,
        ),
        p(
          <>
            A swing horizon is five to sixty-three sessions and does not pay that toll six times a
            day, which is the argument for the swing engine. It is not yet a threshold. The swing
            table stays empty until <Datum>pnpm validate:swing</Datum> says otherwise.
          </>,
        ),
      ],
    },
    {
      heading: "The rows that escape info",
      blocks: [
        table(
          "Signal types that resolve above info",
          ["Type", "Severity", "Why"],
          [
            [
              <Datum key="t">series_to_trade_to_trade</Datum>,
              <Datum key="ts">urgent</Datum>,
              "Trade-to-trade surveillance is the exchange saying something is wrong",
            ],
            [
              <Datum key="h">high_52w_on_volume, low_52w_on_volume</Datum>,
              <Datum key="hs">
                warn, or urgent above {QUOTED.TECHNICAL_URGENT_VOLUME_MULTIPLE}× volume
              </Datum>,
              "A 52-week extreme on far more than the median volume is not noise",
            ],
            [
              <Datum key="c">confluence_bullish, confluence_bearish</Datum>,
              <Datum key="cs">warn</Datum>,
              <>
                <Datum key="cf">{QUOTED.CONFLUENCE_FLOOR}</Datum> distinct strategies agreeing on
                one symbol, one side, one bucket
              </>,
            ],
            [
              <Datum key="s">confluence_bullish_strong, confluence_bearish_strong</Datum>,
              <Datum key="ss">urgent</Datum>,
              <>
                <Datum key="sf">{QUOTED.CONFLUENCE_STRONG_FLOOR}</Datum> or more agreeing, so
                agreement itself is the threshold
              </>,
            ],
          ],
        ),
        p(
          <>
            The confluence rows are the only ones whose severity is fixed at the emitter rather than
            read from a distribution. That is the point of the filter: nobody has to measure what
            agreement is worth to know it means more than one opinion.
          </>,
        ),
      ],
    },
    {
      heading: "A quiet phone is the intended state",
      blocks: [
        p(
          <>
            "No alerts today" is what a working system looks like right now. The muted types are
            muted by decision, so silence is the scheduler agreeing with the measurement rather than
            failing.
          </>,
        ),
        note(
          <>
            When an urgent alert does arrive, it arrives in the evening. The daily technicals are
            written by the <Datum>20:15</Datum> job and the swing rows by the <Datum>20:30</Datum>{" "}
            one, so a quiet phone at <Datum>16:00</Datum> is the end-of-day chain not being due yet.
          </>,
        ),
      ],
    },
  ],
};
